import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase/client';
import {
  BUNDLED_SYSTEM_LANGUAGES,
  getBundledSystemLanguages,
  isBundledSystemLanguage,
  type SystemLanguage,
} from '../lib/i18n/system-languages';

export { getBundledSystemLanguages };
export type { SystemLanguage };

// Complete display-name fallbacks for ALL bundled locales, used when
// normalizing backend rows that may include codes not in the offline fallback list.
const ALL_LOCALE_DISPLAY_FALLBACKS = new Map<string, SystemLanguage>([
  ...BUNDLED_SYSTEM_LANGUAGES.map((l): [string, SystemLanguage] => [l.code, l]),
  ['ckb', { code: 'ckb', combinedName: 'Kurdish (Sorani) (کوردی سۆرانی)', englishName: 'Kurdish (Sorani)', nativeName: 'کوردی سۆرانی', isRTL: true, sortOrder: 60 }],
]);

type SystemLanguageRow = {
  code: string;
  combined_name: string | null;
  is_rtl: boolean | null;
  sort_order: number | null;
};

function splitCombinedName(combinedName: string, fallback: SystemLanguage): Pick<SystemLanguage, 'englishName' | 'nativeName'> {
  const match = combinedName.match(/^(.*)\s+\(([^()]*)\)$/);

  if (!match) {
    return {
      englishName: fallback.englishName,
      nativeName: combinedName || fallback.nativeName,
    };
  }

  return {
    englishName: match[1],
    nativeName: match[2],
  };
}

function normalizeLanguages(rows: SystemLanguageRow[] | null): SystemLanguage[] {
  const languages = (rows ?? [])
    .filter((row): row is SystemLanguageRow & { code: SystemLanguage['code'] } => isBundledSystemLanguage(row.code))
    .map((row) => {
      const fallback = ALL_LOCALE_DISPLAY_FALLBACKS.get(row.code) ?? BUNDLED_SYSTEM_LANGUAGES[0];
      const combinedName = row.combined_name || fallback.combinedName;
      const names = splitCombinedName(combinedName, fallback);

      return {
        code: row.code,
        combinedName,
        englishName: names.englishName,
        nativeName: names.nativeName,
        isRTL: row.is_rtl ?? fallback.isRTL,
        sortOrder: row.sort_order ?? fallback.sortOrder,
      };
    })
    .sort((a, b) => a.sortOrder - b.sortOrder);

  return languages.length > 0 ? languages : BUNDLED_SYSTEM_LANGUAGES;
}

export function useLanguages() {
  return useQuery({
    queryKey: ['system-languages', 'active'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('system_languages')
          .select('code, combined_name, is_rtl, sort_order')
          .order('sort_order', { ascending: true });

        if (error) throw error;

        return normalizeLanguages(data as SystemLanguageRow[]);
      } catch {
        return BUNDLED_SYSTEM_LANGUAGES;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: 'always',
  });
}
